import {useDispatch, useSelector} from 'react-redux'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {readEndpoint} from 'redux-json-api'

import {ethicalWillsLoading, ethicalWillsLoaded, ethicalWillsFailed} from '../../actions/corpMaxActionCreators'
import {cleanEthicalWills} from '../lifeStoryTools'

/**
 * Loads the current user's ethical wills (guided Life Story records) into the
 * redux-json-api store and exposes them as a List of Maps.
 *
 * Fetch status is tracked in the redux store (`ethicalWillsStatus`) rather
 * than inferred from `state.api['ethical-wills']`: redux-json-api leaves no
 * trace of an empty collection, which would otherwise refetch on every mount
 * and delay the hero on each visit to the category page.
 *
 * A failed fetch becomes `ethicalWillsUnavailable` (never 'loaded'), so an
 * empty list can't be mistaken for "no life stories exist". The next mount
 * retries.
 *
 * `skip` suppresses the fetch for callers that only conditionally need the
 * viewer's own collection (e.g. useEverplanEthicalWills on another person's
 * everplan) — hooks can't be called conditionally, but the request can be.
 */
const useEthicalWills = ({skip = false} = {}) => {
  const dispatch = useDispatch()
  const rawEthicalWills = useSelector(state => state.api['ethical-wills'])
  const status = useSelector(state => state.ethicalWillsStatus)

  const refetchEthicalWills = useCallback(
    () => dispatch(readEndpoint('ethical-wills'))
      .then(response => {
        dispatch(ethicalWillsLoaded())

        return response
      })
      .catch(() => dispatch(ethicalWillsFailed())),
    [dispatch]
  )

  // Each mounted instance triggers at most one fetch (the ref guard): this
  // keeps StrictMode's double-invoked effect from firing a duplicate request,
  // and — since `status` is a dependency — keeps the effect from looping on a
  // persistent failure (failed status would otherwise refetch forever). A
  // 'failed' status still retries, but only on the next mount.
  const fetchAttemptedRef = useRef(false)

  useEffect(() => {
    if (skip || fetchAttemptedRef.current) return
    if (status !== 'initial' && status !== 'failed') return

    fetchAttemptedRef.current = true
    dispatch(ethicalWillsLoading())
    refetchEthicalWills()
  }, [skip, status, dispatch, refetchEthicalWills])

  // Stable between store updates: callers key effects on this List (e.g.
  // LifeStoryFlow's begin-path guard), and a fresh one every render re-runs
  // them on every render — a navigation inside one loops forever.
  const ethicalWills = useMemo(() => cleanEthicalWills(rawEthicalWills), [rawEthicalWills])

  return {
    loadingEthicalWills: status === 'initial' || status === 'loading',
    ethicalWillsUnavailable: status === 'failed',
    ethicalWills,
    refetchEthicalWills
  }
}

export default useEthicalWills
